import React, { ComponentProps, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import {
  IngredientsRoot,
  BannerText,
  IngredientsWrapper,
  IngredientsCard,
  CardImage,
  CardText,
  Container,
  Footer,
  MobileWrapper,
  SliderContainer,
  BorderWrapper,
} from './Ingredients.styles'
import { SectionTitle, Subtitle } from '../styles'
import Image, { StaticImageData } from 'next/image'
import banner from '../../../../../../public/assets/recipes/categories/individual/ingredients-banner.png'
import venison from '../../../../../../public/assets/recipes/categories/individual/venison.png'
import heart from '../../../../../../public/assets/recipes/categories/individual/heart-ingredient.png'
import liver from '../../../../../../public/assets/recipes/categories/individual/liver.png'
import kidney from '../../../../../../public/assets/recipes/categories/individual/kidney.png'
import bone from '../../../../../../public/assets/recipes/categories/individual/bone.png'
import vitamin from '../../../../../../public/assets/recipes/categories/individual/vitamin-mineral-mix.png'
import chickenBacks from '../../../../../../public/assets/recipes/categories/individual/chicken-backs.png'
import legs from '../../../../../../public/assets/recipes/categories/individual/chicken-leg-quarters.png'
import duck from '../../../../../../public/assets/recipes/categories/individual/duck.png'
import turkeyTails from '../../../../../../public/assets/recipes/categories/individual/turkey-tails.png'
import lamb from '../../../../../../public/assets/recipes/categories/individual/lamb.png'
import gizzards from '../../../../../../public/assets/recipes/categories/individual/gizzards.png'
import { Text } from 'components/Text'
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import { sliderSettings } from 'fixtures/slider'
import Slider from 'react-slick'
import { BorderWave } from 'components/BorderWave'
import { ColorTokens } from 'stitches.config'
import { TranslationKeys } from 'locales/translationKeys'

type IngredientsProps = {
  recipe: string
  ingredients: IngredientType[]
  hideWave?: boolean
  background: ColorTokens
}

export type IngredientType = {
  label: TranslationKeys
  info: TranslationKeys
  src: StaticImageData
  icon: any
}

export const IngredientMap: Record<string, IngredientType> = {
  venison: {
    label: 'recipes:individual.ingredients.venison.label',
    info: 'recipes:individual.ingredients.venison.info',
    src: venison,
    icon: 'venison',
  },
  beefHeart: {
    label: 'recipes:individual.ingredients.beef-heart.label',
    info: 'recipes:individual.ingredients.beef-heart.info',
    src: heart,
    icon: 'heart',
  },
  beefLiver: {
    label: 'recipes:individual.ingredients.beef-liver.label',
    info: 'recipes:individual.ingredients.beef-liver.info',
    src: liver,
    icon: 'liver',
  },
  beefKidney: {
    label: 'recipes:individual.ingredients.beef-kidney.label',
    info: 'recipes:individual.ingredients.beef-kidney.info',
    src: kidney,
    icon: 'kidney',
  },
  beefBone: {
    label: 'recipes:individual.ingredients.beef-bone.label',
    info: 'recipes:individual.ingredients.beef-bone.info',
    src: bone,
    icon: 'bone',
  },
  vitamin: {
    label: 'recipes:individual.ingredients.vitamin.label',
    info: 'recipes:individual.ingredients.vitamin.info',
    src: vitamin,
    icon: 'vitamin',
  },
  chickenHeart: {
    label: 'recipes:individual.ingredients.chicken-heart.label',
    info: 'recipes:individual.ingredients.chicken-heart.info',
    src: heart,
    icon: 'heart',
  },
  chickenBacks: {
    label: 'recipes:individual.ingredients.chicken-backs.label',
    info: 'recipes:individual.ingredients.chicken-backs.info',
    src: chickenBacks,
    icon: 'chicken-backs',
  },
  chickenLegs: {
    label: 'recipes:individual.ingredients.chicken-leg.label',
    info: 'recipes:individual.ingredients.chicken-leg.info',
    src: legs,
    icon: 'legs',
  },
  chickenLiver: {
    label: 'recipes:individual.ingredients.chicken-liver.label',
    info: 'recipes:individual.ingredients.chicken-liver.info',
    src: liver,
    icon: 'liver',
  },
  duck: {
    label: 'recipes:individual.ingredients.duck.label',
    info: 'recipes:individual.ingredients.duck.info',
    src: duck,
    icon: 'duck',
  },
  turkeyGizzards: {
    label: 'recipes:individual.ingredients.turkey-gizzards.label',
    info: 'recipes:individual.ingredients.turkey-gizzards.info',
    src: gizzards,
    icon: 'gizzards',
  },
  turkeyTails: {
    label: 'recipes:individual.ingredients.turkey-tails.label',
    info: 'recipes:individual.ingredients.turkey-tails.info',
    src: turkeyTails,
    icon: 'turkey-tails',
  },
  turkeyLiver: {
    label: 'recipes:individual.ingredients.turkey-liver.label',
    info: 'recipes:individual.ingredients.turkey-liver.info',
    src: liver,
    icon: 'liver',
  },
  turkeyWings: {
    label: 'recipes:individual.ingredients.turkey-wings.label',
    info: 'recipes:individual.ingredients.turkey-wings.info',
    src: legs,
    icon: 'legs',
  },
  lamb: {
    label: 'recipes:individual.ingredients.lamb.label',
    info: 'recipes:individual.ingredients.lamb.info',
    src: lamb,
    icon: 'lamb',
  },
}

export const Ingredients = ({ recipe, ingredients, hideWave = false, background }: IngredientsProps) => {
  const { t } = useTranslation()

  const footer = t(`recipes:${recipe}.ingredients.footer`)

  return (
    <>
      {!hideWave && <BorderWrapper>
        <BorderWave
          bottom
          background="secondary-150"
          svgHeight="90px"
          invert
          svgWidth="calc(144% + 1.3px)"
          path="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
        />
      </BorderWrapper>}
      <IngredientsRoot 
        padding={hideWave} 
      css={{background: `$${background}`}}>
        <Container>
          <BannerText>
            <SectionTitle align="center">{t('recipes:individual.ingredients.title')}</SectionTitle>
            <Subtitle>{t(`recipes:${recipe}.ingredients.subtitle`)}</Subtitle>
          </BannerText>
          <IngredientsWrapper scrollable={ingredients.length > 5}>
            {ingredients.map((ingredient, index) => (
              <IngredientsCard key={index}>
                <CardImage icon={ingredient.icon}>
                  <Image src={ingredient.src} alt={ingredient.label} />
                </CardImage>
                <CardText>
                  <Text size="bodyExtraLarge" weight="medium" color="primary-450">
                    {t(`${ingredient.label}`)}
                  </Text>
                  <Text size="bodyLarge" color="quaternary-200">
                    {t(`${ingredient.info}`)}
                  </Text>
                </CardText>
              </IngredientsCard>
            ))}
          </IngredientsWrapper>
          <MobileWrapper>
            <SectionTitle align="center">{t('recipes:individual.ingredients.title')}</SectionTitle>
            <Subtitle css={{ maxWidth: '240px', margin: 'auto', marginTop: '16px' }}>
              {t(`recipes:${recipe}.ingredients.subtitle`)}
            </Subtitle>
            <SliderContainer>
              <Slider {...sliderSettings}>
                {ingredients.map((ingredient, index) => (
                  <IngredientsCard key={index}>
                    <CardImage icon={ingredient.icon}>
                      <Image src={ingredient.src} alt={ingredient.label} />
                    </CardImage>
                    <CardText>
                      <Text
                        align={{ '@initial': 'center', '@bp3': 'left' }}
                        size="bodyExtraLarge"
                        weight="medium"
                        color="primary-450"
                      >
                        {t(`${ingredient.label}`)}
                      </Text>
                      <Text
                        align={{ '@initial': 'center', '@bp3': 'left' }}
                        size="body"
                        color="quaternary-200"
                      >
                        {t(`${ingredient.info}`)}
                      </Text>
                    </CardText>
                  </IngredientsCard>
                ))}
              </Slider>
            </SliderContainer>
          </MobileWrapper>
          {footer && <Footer>{footer}</Footer>}
        </Container>
      </IngredientsRoot>
    </>
  )
}

Ingredients.displayName = 'Ingredients'
